import { useCallback, useRef, useState } from 'react';

import type React from 'react';

export type ListReorderDrag = {
  /** Index of the row being dragged, or null when no drag is in flight. */
  draggingIndex: number | null;
  /** The gap a drop would land in, 0..count, or null. */
  dropIndex: number | null;
  handleDragStart: (e: React.DragEvent, index: number) => void;
  /** A row: above its midpoint inserts before it, below inserts after. */
  handleDragOver: (e: React.DragEvent, index: number) => void;
  /** The list container: clears the target when the pointer leaves the list. */
  handleListDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragEnd: () => void;
  /**
   * Which edge of row `index` draws the insertion line. Null for the gaps on
   * either side of the dragged row, where a drop would change nothing.
   */
  dropEdgeFor: (index: number) => 'above' | 'below' | null;
};

/**
 * Native HTML5 drag reordering for a flat list, in the same shape as the Reel
 * Editor's `useReelEditorDrag` (no library). Drop targets are the gaps between
 * rows, not the rows themselves: a drop on a row is ambiguous about before or
 * after, whereas an insertion line makes the outcome legible before release.
 *
 * The hook owns only the gesture. Moving the item is the caller's job through
 * `onMove(from, to)`, where `to` is the item's index in the list after the move.
 */
export function useListReorderDrag(
  count: number,
  onMove: (from: number, to: number) => void,
): ListReorderDrag {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const sourceRef = useRef<number | null>(null);
  // Read on drop: the target is state, and the drop handler is a callback the
  // rows captured on an earlier render.
  const dropRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    sourceRef.current = index;
    setDraggingIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox refuses to start a drag without payload on the transfer.
      e.dataTransfer.setData('text/plain', `list-row:${index}`);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (sourceRef.current === null) return;
    // `preventDefault` is what makes the row droppable at all.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const gap = e.clientY > rect.top + rect.height / 2 ? index + 1 : index;
    if (gap === dropRef.current) return;
    dropRef.current = gap;
    setDropIndex(gap);
  }, []);

  const handleListDragLeave = useCallback((e: React.DragEvent) => {
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    dropRef.current = null;
    setDropIndex(null);
  }, []);

  const reset = useCallback(() => {
    sourceRef.current = null;
    dropRef.current = null;
    setDraggingIndex(null);
    setDropIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const from = sourceRef.current;
      const gap = dropRef.current;
      reset();
      if (from === null || gap === null) return;
      // Taking the dragged row out shifts every gap after it up by one.
      const to = gap > from ? gap - 1 : gap;
      if (to !== from) onMove(from, to);
    },
    [onMove, reset],
  );

  const dropEdgeFor = useCallback(
    (index: number): 'above' | 'below' | null => {
      const from = draggingIndex;
      if (from === null || dropIndex === null) return null;
      if (dropIndex === from || dropIndex === from + 1) return null;
      if (dropIndex === index) return 'above';
      if (dropIndex === count && index === count - 1) return 'below';
      return null;
    },
    [count, draggingIndex, dropIndex],
  );

  return {
    draggingIndex,
    dropIndex,
    handleDragStart,
    handleDragOver,
    handleListDragLeave,
    handleDrop,
    handleDragEnd: reset,
    dropEdgeFor,
  };
}
